// A mixin providing shared functionality and computed data for each of the vehicle selector components
import axios from 'axios';
import analytics, { GTM } from 'fcoModules/analytics';
import { mapGetters, mapState } from 'vuex';
import { isRequestIdle, isRequestPending } from '../store/request-status';
import analyticsMixin from './analyticsMixin';
import featureFlagsMixin from './featureFlagsMixin';
import { addVehicleToQuote } from '../services/quoteService';

export default {
    emits: ['vehicle-selected'],
    mixins: [analyticsMixin, featureFlagsMixin],
    props: {
        promptAboutQuote: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            GTM,
            userWantsExistingQuote: false,
        };
    },
    computed: {
        ...mapState({
            productsInCurrentQuote: (state) => state.miniQuote.quoteDetails.totalItems > 0,
            isSPA: ({ isSPA }) => isSPA,
        }),
        ...mapGetters(['isAuthorizedUser']),
        ...mapState('vehicleSelector', {
            vsData: (state) => state.vsData,
            forceUrl: (state) => state.forceUrl,
            isOpenDefault: (state) => state.isOpenDefault,
            partTypePendingForceCar: (state) => state.partTypePendingForceCar,
            saveLookupBeforeRedirect: (state) => state.saveLookupBeforeRedirect,
            partsToSaveBeforeRedirect: (state) => state.partsToSaveBeforeRedirect,
            isVSDataIdle: (state) => isRequestIdle(state.requests.getVehicleSelectorData),
            isVSDataPending: (state) => isRequestPending(state.requests.getVehicleSelectorData),
            preventNavigationOnVehicleUpdate: (state) => state.preventNavigationOnVehicleUpdate,
        }),
        ...mapState('miniQuote', {
            worksheetId: (state) => state.quoteDetails.worksheetHeaderId,
        }),
        keepExistingQuoteFinal() {
            // if we are not prompting the user than we assume it should always be a new quote
            return this.promptAboutQuote ? this.userWantsExistingQuote : true;
        },
    },
    methods: {
        async addVehicleToQuote(vehicle, worksheetId) {
            const {
                data: {
                    worksheetVehicleSID: { id },
                },
            } = await addVehicleToQuote(vehicle, worksheetId);

            return { ...vehicle, id };
        },
        async selectVehicleFromVehicleData({ baseVehicleId, engineId: engine, submodelId: submodel }, keepQuote = false) {
            const engineId = Number(engine) ? engine : undefined;
            const submodelId = Number(submodel) ? submodel : undefined;

            if (!this.isEntSearchEnabled) {
                const {
                    data: { vehicle },
                } = await axios.post(this.fcoUrl('/vehicle/select/v2/'), { baseVehicleId, engineId, submodelId, quote: keepQuote });
                return vehicle;
            }

            if (!keepQuote) await this.$store.dispatch('miniQuote/newQuote');

            const {
                data: { vehicle },
            } = await axios.post(this.fcoUrl(`/vehicle/select/v3/?worksheetId=${this.worksheetId}`), { baseVehicleId, engineId, submodelId });

            return vehicle;
        },
        async selectVehicleFromVIN({ vin, plate, state }, keepQuote = false) {
            if (!this.isEntSearchEnabled) {
                const {
                    data: { vehicle },
                } = await axios.post(this.fcoUrl(`/vehicle/select/defaultByVin?quote=${keepQuote}`), { vin });
                return vehicle;
            }

            const requests = [axios.post(this.fcoUrl(`/vehicle/select/v2/defaultByVin`), { vin })];

            if (!keepQuote) requests.push(this.$store.dispatch('miniQuote/newQuote'));

            const [vehicleResponse] = await Promise.all(requests);

            const vehicle = {
                ...vehicleResponse.data,
                plate,
                state,
            };

            return this.addVehicleToQuote(vehicle, this.worksheetId);
        },
        async selectByWorksheetVehicleId(worksheetVehicleId, keepQuote = false) {
            if (!this.isEntSearchEnabled) {
                const {
                    data: { vehicle },
                } = await axios.post(this.fcoUrl(`/vehicle/select/history/${worksheetVehicleId}/select?quote=${keepQuote}`));
                return vehicle;
            }

            const requests = [axios.get(this.fcoUrl(`/vehicle/select/vehicleByWorksheetVehicleId?worksheetVehicleId=${worksheetVehicleId}`))];

            if (!keepQuote) requests.push(this.$store.dispatch('miniQuote/newQuote'));

            const [{ data: vehicle }] = await Promise.all(requests);

            return this.addVehicleToQuote(vehicle, this.worksheetId);
        },
        handleVehicleSelectSuccess({ vehicle = {}, method = '' }) {
            /*
                the currentVehicle watcher in VehicleSelector.vue sets the forceUrl to false when a vehicle is added,
                so by the time the analytics() callback is called below, this.forceUrl is false.
                Setting it to a const here solves the problem, so we capture the original value at the time of selection.
                However, this race condition may rear its head again, due to watchers in other components,
                so it may be worth investigating a different approach to force-car where this race condition is eliminated.
            */
            const { forceUrl } = this;
            this.$emit('vehicle-selected', vehicle);
            this.$store.commit('vehicleSelector/setCurrentVehicle', vehicle);

            if (!this.isEntSearchEnabled) {
                // We're doing this because the current vehicle is tightly coupled to a worksheet on the BE (for now).
                // If a new vehicle is selected, it immediately creates a new worksheet.
                this.$store.dispatch('miniQuote/getQuoteData');
            }

            this.$_analyticsMixin_trackVehicleAdded(method);

            // if a part type selection is pending then we need to just update the vehicle selected and allow
            // the other component the ability to handle that selection without redirects interfering
            if (this.partTypePendingForceCar) {
                // set it back to false so component watcher knows it can attempt selection again
                this.$store.dispatch('vehicleSelector/setVsProp', { prop: 'partTypePendingForceCar', value: false });
                return;
            }

            // this needs to happen even if preventNavigationOnVehicleUpdate is true
            if (forceUrl) {
                let loading;

                if (!this.isSPA) {
                    loading = this.$fcoLoading();
                }

                if (this.saveLookupBeforeRedirect) {
                    this.$store.dispatch('partSelection/savePartLookup', { partTypes: this.partsToSaveBeforeRedirect, vehicle });
                }

                document.dispatchEvent(new CustomEvent('vehicleSelector/forceSelected'));

                // forceUrl can just a boolean to trigger the force car state without redirecting
                if (forceUrl === true) {
                    if (loading) loading.remove();
                    return;
                }

                if (!this.isSPA) {
                    window.location.href = this.fcoUrl(forceUrl);
                    return;
                }

                this.$router.push(forceUrl);
                return;
            }

            if (!this.preventNavigationOnVehicleUpdate) {
                if (!this.isSPA) {
                    window.location.href = this.fcoUrl('/catalog/browse.html');
                    return;
                }
                this.$router.push('/catalog/browse');
                return;
            }

            // for any pages where the VS is closed by default, we want to close it after a vehicle update is made
            if (!this.isOpenDefault) {
                this.$store.dispatch('vehicleSelector/setVsProp', { prop: 'isOpen', value: false });
            }
        },
        analytics,
    },
    watch: {
        isAuthorizedUser: {
            handler() {
                if (this.isAuthorizedUser) {
                    this.$store.dispatch('requestIfIdle', ['vehicleSelector/getVehicleSelectorData']);
                }
            },
            immediate: true,
        },
    },
};
